// 该文件为全局 WebSocket 管理模块
// 作用：统一管理 WebSocket 连接、鉴权、断线重连、消息分发、主动断开等
// 用法说明：
//   1. 登录成功后调用 websocketManager.connect(token, userId)
//   2. 需要实时消息的页面/组件通过 websocketManager.onMessage 订阅消息
//   3. 登出时调用 websocketManager.disconnect()
//   4. 支持 onStatusChange 订阅连接状态
// 被调用文件：LoginView.vue、NavView.vue、DeviceManagement.vue 等

import { ref } from "vue";
import config from "@/config";
import jsCookie from "js-cookie";
import { logger } from "@/utils/logger";
import store from "@/store";

// WebSocket 状态类型
type WebSocketStatus =
  | "Disconnected"
  | "Connecting"
  | "Connected"
  | "Reconnecting";

// 消息回调类型
type MessageCallback = (msg: any) => void;
// 状态回调类型
type StatusCallback = (status: WebSocketStatus) => void;

class WebSocketManager {
  private socket: WebSocket | null = null;
  private status = ref<WebSocketStatus>("Disconnected");
  private reconnectAttempts = 0;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private token: string | null = null;
  private userId: string | null = null;
  private messageCallbacks: Set<MessageCallback> = new Set();
  private statusCallbacks: Set<StatusCallback> = new Set();
  private reconnectTimer: any = null;

  // 添加消息去重机制
  private processedMessages: Set<string> = new Set();
  private readonly MESSAGE_DEDUP_TIMEOUT = 5000; // 5秒去重窗口

  constructor() {
    // 在构造函数中尝试恢复凭据
    this._restoreCredentials();
  }

  // 连接 WebSocket
  public connect(token: string, userId: string) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      logger.warn("WebSocket已连接，跳过重复连接");
      return;
    }

    if (this.socket?.readyState === WebSocket.CONNECTING) {
      logger.warn("WebSocket正在连接中，跳过重复连接");
      return;
    }

    this.token = token;
    this.userId = userId;
    this._saveCredentials();
    this._initializeWebSocket();
  }

  // 自动连接（如果已有凭据）
  public autoConnect() {
    const tenantIdFromStore = (store.getters as any)?.getUser?.tenant_id;
    const tenantIdFromCookie = jsCookie.get("tenantId");
    const hasTenant = tenantIdFromCookie ?? tenantIdFromStore;
    if (
      this.token &&
      this.userId &&
      hasTenant &&
      this.status.value === "Disconnected"
    ) {
      logger.info("Auto-connecting WebSocket...");
      this._initializeWebSocket();
    }
  }

  // 断开 WebSocket
  public disconnect() {
    if (this.socket) {
      this.socket.close(1000, "Manual disconnect");
      this.socket = null;
      this.status.value = "Disconnected";
      this._emitStatus();
      logger.info("WebSocket disconnected manually");
    }
    this._clearReconnect();
  }

  // 发送消息
  public send(msg: any) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        const messageStr = typeof msg === "string" ? msg : JSON.stringify(msg);
        this.socket.send(messageStr);
        logger.debug("WebSocket消息已发送:", messageStr);
      } catch (error) {
        logger.error("发送WebSocket消息失败:", error);
        throw new Error("发送消息失败");
      }
    } else {
      logger.error("WebSocket未连接，当前状态:", this.status.value);
      throw new Error("WebSocket未连接");
    }
  }

  // 监听消息
  public onMessage(callback: MessageCallback): () => void {
    // 检查是否已存在相同的回调函数
    for (const existingCallback of this.messageCallbacks) {
      if (existingCallback === callback) {
        logger.warn("检测到重复的消息监听器，跳过添加");
        return () => this.messageCallbacks.delete(existingCallback);
      }
    }

    this.messageCallbacks.add(callback);
    logger.debug(
      `添加消息监听器，当前监听器数量: ${this.messageCallbacks.size}`
    );

    return () => {
      this.messageCallbacks.delete(callback);
      logger.debug(
        `移除消息监听器，当前监听器数量: ${this.messageCallbacks.size}`
      );
    };
  }

  // 订阅状态
  public onStatusChange(cb: StatusCallback) {
    this.statusCallbacks.add(cb);
    return () => this.statusCallbacks.delete(cb);
  }

  // 获取当前状态
  public getStatus() {
    return this.status.value;
  }

  // 更新token
  public updateToken(newToken: string) {
    if (newToken && newToken !== this.token) {
      this.token = newToken;
      this._saveCredentials();
      logger.info("WebSocket token updated");

      // 如果当前连接已断开，重新连接
      if (this.status.value === "Disconnected" && this.userId) {
        logger.info("Reconnecting WebSocket with new token...");
        this._initializeWebSocket();
      }
    }
  }

  // 内部：初始化 WebSocket
  private _initializeWebSocket() {
    if (!this.token || !this.userId) {
      logger.error("Missing user credentials for WebSocket");
      this.status.value = "Disconnected";
      this._emitStatus();
      return;
    }
    this.status.value =
      this.reconnectAttempts > 0 ? "Reconnecting" : "Connecting";
    this._emitStatus();
    const tenantIdFromStore = (store.getters as any)?.getUser?.tenant_id;
    const tenantIdFromCookie = jsCookie.get("tenantId");
    const rawTenantId = tenantIdFromCookie ?? tenantIdFromStore;
    const tenantId = rawTenantId ? String(rawTenantId) : undefined;
    const wsUrl = tenantId
      ? `${config.wssUrl}/${tenantId}/${this.userId}`
      : `${config.wssUrl}/${this.userId}`;
    logger.info("Connecting WebSocket to", wsUrl);
    this.socket = new WebSocket(wsUrl);

    this.socket.onopen = () => {
      this.status.value = "Connected";
      this.reconnectAttempts = 0;
      this._emitStatus();
      logger.info("WebSocket connected");
      // 不需要发送鉴权消息，因为登录时已经验证过了
    };

    this.socket.onclose = (event) => {
      this.status.value = "Disconnected";
      this._emitStatus();
      logger.info("WebSocket disconnected, code:", event.code);
      // 1000: 正常关闭，1001: 终端离开，1005: 未收到预期状态码
      const normalCloseCodes = [1000, 1001, 1005];
      if (!normalCloseCodes.includes(event.code)) {
        this._reconnectWebSocket();
      } else {
        logger.info("WebSocket closed normally, no reconnection needed");
      }
    };

    this.socket.onerror = (error) => {
      logger.error("WebSocket error: ", error);
    };

    this.socket.onmessage = (event) => {
      let msg: any;
      try {
        msg = JSON.parse(event.data);
      } catch {
        msg = event.data;
      }

      logger.debug(
        `[WebSocket] 收到消息，当前监听器数量: ${this.messageCallbacks.size}`
      );
      logger.debug(`[WebSocket] 消息内容: ${JSON.stringify(msg)}`);

      // 处理 token 验证失败
      if (msg?.type === "error" && msg?.action === "auth_failed") {
        this.socket?.close(1000, "Auth failed");
        this.status.value = "Disconnected";
        this._emitStatus();
        // 可在此处触发全局登出
        window.dispatchEvent(new CustomEvent("user-logout"));
        return;
      }

      // 处理强制登出（单点登录踢下线）
      if (msg?.type === "system" && msg?.action === "force_logout") {
        logger.info("收到强制登出通知:", msg.message);
        this.socket?.close(1000, "Force logout");
        this.status.value = "Disconnected";
        this._emitStatus();
        // 触发强制登出事件
        window.dispatchEvent(
          new CustomEvent("user-force-logout", {
            detail: { message: msg.message },
          })
        );
        return;
      }

      // 消息去重检查
      const messageKey = this._generateMessageKey(msg);
      if (this.processedMessages.has(messageKey)) {
        logger.warn(`[WebSocket] 检测到重复消息，跳过处理: ${messageKey}`);
        return;
      }

      // 添加到已处理消息集合
      this.processedMessages.add(messageKey);

      // 5秒后清理消息记录
      setTimeout(() => {
        this.processedMessages.delete(messageKey);
      }, this.MESSAGE_DEDUP_TIMEOUT);

      // 遍历所有监听器处理消息
      let callbackIndex = 0;
      this.messageCallbacks.forEach((cb) => {
        callbackIndex++;
        logger.debug(
          `[WebSocket] 调用监听器 ${callbackIndex}/${this.messageCallbacks.size}`
        );
        try {
          cb(msg);
        } catch (error) {
          logger.error(
            `[WebSocket] 监听器 ${callbackIndex} 处理消息时出错:`,
            error
          );
        }
      });
    };
  }

  // 内部：重连
  private _reconnectWebSocket() {
    if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      logger.error("Max WebSocket reconnect attempts reached");
      this.status.value = "Disconnected";
      this._emitStatus();
      return;
    }
    this.reconnectAttempts++;
    this.status.value = "Reconnecting";
    this._emitStatus();
    logger.info(`WebSocket reconnecting... attempt ${this.reconnectAttempts}`);
    this._clearReconnect();
    this.reconnectTimer = setTimeout(() => {
      this._initializeWebSocket();
    }, 2000 * this.reconnectAttempts); // 递增延迟
  }

  // 内部：清理重连定时器
  private _clearReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // 内部：触发状态回调
  private _emitStatus() {
    this.statusCallbacks.forEach((cb) => cb(this.status.value));
  }

  // 内部：生成消息唯一标识
  private _generateMessageKey(msg: any): string {
    // 使用消息的关键字段生成唯一标识
    const keyFields = {
      type: msg?.type,
      action: msg?.action,
      messageId: msg?.messageId,
      event_id: msg?.data?.event_id,
      device_id: msg?.data?.device_id,
      timestamp: msg?.timestamp || msg?.data?.timestamp,
    };

    return JSON.stringify(keyFields);
  }

  private _saveCredentials() {
    try {
      jsCookie.set("userToken", this.token!, { expires: 7 }); // 7天过期
      jsCookie.set("username", this.userId!, { expires: 7 }); // 7天过期
    } catch (e) {
      logger.error("Failed to save credentials to cookies:", e);
      // fallback: localStorage
      localStorage.setItem("userToken", this.token!);
      localStorage.setItem("username", this.userId!);
    }
  }

  private _restoreCredentials() {
    try {
      const token = jsCookie.get("userToken");
      const userId = jsCookie.get("username");
      if (token && userId) {
        this.token = token;
        this.userId = userId;
        return;
      }
    } catch (e) {
      // fallback: localStorage
      const token = localStorage.getItem("userToken");
      const userId = localStorage.getItem("lastLoginUsername");
      if (token && userId) {
        this.token = token;
        this.userId = userId;
      }
    }
  }
}

// 单例导出
const websocketManager = new WebSocketManager();
websocketManager.autoConnect(); // 自动尝试连接
export default websocketManager;
